import {
  BIDDER_PRESETS,
  BIDDER_PRESET_BLURBS,
  BIDDER_PRESET_LABELS,
  resolveBidderPreset,
} from '@auctisync/shared';
import { Button, LearnMoreLink } from '@auctisync/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { HouseSelector } from '../../components/HouseSelector';
import { LogoUploader } from '../../components/LogoUploader';
import { useToast } from '../../components/Toast';
import { useOwnedTenants } from '../../hooks/useOwnedTenants';
import {
  brandingArgs,
  EMPTY_FORM,
  formFromBranding,
  hasErrors,
  isDirty,
  normaliseHex,
  previewFromForm,
  validateBranding,
  type BrandingForm,
} from '../../lib/brandingForm';
import { supabase } from '../../lib/supabase';
import type { TenantBranding } from '@auctisync/color';

/**
 * Tenant admin — Branding (owner-only).
 *
 * WHY THIS PAGE EXISTS. Branding could only ever be set ONCE, in the onboarding
 * copilot's colour card, and never changed again. A house that rebranded, or
 * that picked a colour badly on day one, had no way back. Jim, 2026-09-08, on
 * the art-direction presets: *"they should also be editable later."* The preset
 * got its home first; the LOGO and the three COLOURS joined it on 2026-09-14,
 * once the logo started appearing in the header of every screen of the app and
 * "set it at onboarding and live with it" stopped being tenable.
 *
 * WHAT A PRESET IS. One of three art directions over ONE layout: it changes type
 * and shape — heading font, label treatment, corner radius, section rhythm — and
 * never colour, and never the position of a box. So a house cannot break its own
 * site with this control, and cannot reach the locked auction colour semantics
 * (amber live, red closing and outbid, green winning), which no tenant may
 * override on any surface.
 *
 * ⚠️ THIS IS PUBLIC. Everything set here renders on the house's own bidder site
 * to anyone who visits, signed in or not — `get_house_identity` publishes
 * `metadata.branding` to anonymous callers so a visitor arriving cold on a lot
 * sees whose sale it is. Nothing private belongs on this page.
 *
 * 🔴 THE PREVIEW IS DERIVED, NOT DRAWN. Every colour in it comes from
 * `brandingToTokens` — the same function the bidder website and the native app
 * call. A preview that computed its own dark panel or its own CTA would be a
 * third opinion, and it would be the one the owner trusted while choosing. See
 * `previewFromForm` in `lib/brandingForm.ts`.
 */

type BrandingRpc = (
  fn: 'set_branding',
  args: {
    p_tenant_id?: string;
    p_primary_color?: string | null;
    p_accent_color?: string | null;
    p_logo_url?: string | null;
    p_preset?: string | null;
    p_deep_color?: string | null;
  },
) => Promise<{ data: unknown; error: { message: string } | null }>;

const brandingRpc = supabase.rpc.bind(supabase) as unknown as BrandingRpc;

const CARD: React.CSSProperties = {
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-card, 0.5rem)',
  padding: '1.25rem',
  background: 'var(--surface)',
  marginBottom: '1rem',
};

export function AdminBranding() {
  const { tenants, selectedId, setSelectedId, loading: tenantsLoading } = useOwnedTenants();
  const toast = useToast();

  const [form, setForm] = useState<BrandingForm>(EMPTY_FORM);
  const [saved, setSaved] = useState<BrandingForm>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (tenantId: string) => {
    setLoading(true);
    setError(null);
    // Read the branding bag straight off the row rather than through
    // `get_house_identity`: that reader is the ANON projection and exists to
    // answer "whose sale is this", not to drive an owner's settings form.
    const { data, error: err } = await supabase
      .from('tenants')
      .select('metadata')
      .eq('id', tenantId)
      .maybeSingle();
    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }
    const branding = (data?.metadata as { branding?: TenantBranding & { preset?: unknown } } | null)
      ?.branding;
    const next = formFromBranding(branding ?? null, resolveBidderPreset(branding?.preset));
    setForm(next);
    setSaved(next);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (selectedId === null) return;
    void load(selectedId);
  }, [selectedId, load]);

  const errors = useMemo(() => validateBranding(form), [form]);
  const preview = useMemo(() => previewFromForm(form), [form]);
  const dirty = isDirty(form, saved);
  const blocked = saving || uploading || hasErrors(errors) || !dirty;

  function set<K extends keyof BrandingForm>(key: K, value: BrandingForm[K]): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function save() {
    if (selectedId === null || hasErrors(errors)) return;
    setSaving(true);
    setError(null);
    // Only genuinely-changed fields are sent: the RPC reads a null argument as
    // "leave unchanged" and an empty string as "clear", so a narrow call cannot
    // rewrite a key this form never showed the owner.
    const { error: err } = await brandingRpc('set_branding', brandingArgs(selectedId, form, saved));
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setSaved(form);
    toast.success('Branding saved. Your bidder site and app update on the next load.');
  }

  return (
    <div style={{ maxWidth: '52rem' }}>
      <header style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>Branding</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '0.35rem' }}>
          Your logo, your colours, and how your bidder site looks. Everything here is public.{' '}
          <LearnMoreLink helpSlug="branding-and-look" newTab>
            About branding
          </LearnMoreLink>
        </p>
      </header>

      {tenants.length > 1 ? (
        <div style={{ marginBottom: '1.25rem' }}>
          <HouseSelector tenants={tenants} selectedId={selectedId} onSelect={setSelectedId} />
        </div>
      ) : null}

      {tenantsLoading || loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading…</p>
      ) : (
        <>
          {/* ─── Logo ───────────────────────────────────────────────────── */}
          <section style={CARD}>
            <h2 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>Your logo</h2>
            <p
              style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0.35rem 0 1rem' }}
            >
              Shown across the top of every screen of your bidder app and on your bidder site. It is
              used exactly as you supply it — we never crop it or change its shape. If you do not
              add one, bidders see your house name instead.
            </p>

            <LogoUploader
              logoUrl={form.logoUrl === '' ? null : form.logoUrl}
              onLogoUrl={(url) => {
                set('logoUrl', url);
              }}
              onUploadingChange={setUploading}
              busy={saving}
              tenantId={selectedId}
              label="Logo"
              /* 🔴 FILLS BLANKS ONLY, and on this page that matters more than in
                 onboarding: a house here has ALREADY chosen colours, and most
                 logo uploads are a swap rather than a first pick. Overwriting a
                 hex somebody deliberately set — because they changed their
                 logo — would be a silent rebrand. Reads `f` inside the updater
                 so all three decisions see the same state. */
              onColoursDetected={(c) => {
                setForm((f) => ({
                  ...f,
                  ...(f.primaryColor.trim() === '' && { primaryColor: c.primary }),
                  ...(f.accentColor.trim() === '' && { accentColor: c.accent }),
                  ...(f.darkColor.trim() === '' && { darkColor: c.dark }),
                }));
              }}
            />

            {form.logoUrl !== '' ? (
              <button
                type="button"
                onClick={() => {
                  set('logoUrl', '');
                }}
                disabled={saving}
                style={{
                  marginTop: '0.6rem',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: 'var(--text-muted)',
                  fontSize: '0.8rem',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                Remove logo
              </button>
            ) : null}
          </section>

          {/* ─── Colours ────────────────────────────────────────────────── */}
          <section style={CARD}>
            <h2 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>
              Your colours
            </h2>
            <p
              style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0.35rem 0 1rem' }}
            >
              We work out the rest — shading, link colours, and the text that sits on each of these
              — and we guarantee the text stays readable on whatever you pick.
            </p>

            <ColourField
              label="Primary colour"
              hint="The band across the top of every page, and the colour your site is built from."
              value={form.primaryColor}
              onChange={(v) => {
                set('primaryColor', v);
              }}
              error={errors.primaryColor}
              disabled={saving}
              testId="branding-primary"
            />
            <ColourField
              label="Accent colour"
              hint="Your buttons. Leave blank to use your main colour."
              value={form.accentColor}
              onChange={(v) => {
                set('accentColor', v);
              }}
              error={errors.accentColor}
              disabled={saving}
              clearable
              testId="branding-accent"
            />
            <ColourField
              label="Secondary colour"
              hint={
                form.darkColor.trim() === ''
                  ? 'Panels like the sale header. Blank means we darken your main colour for you.'
                  : 'Panels like the sale header. Clear it to let us darken your main colour instead.'
              }
              value={form.darkColor}
              onChange={(v) => {
                set('darkColor', v);
              }}
              error={errors.darkColor}
              disabled={saving}
              clearable
              testId="branding-dark"
            />
          </section>

          {/* ─── Preview ────────────────────────────────────────────────── */}
          {preview !== null ? (
            <section style={CARD} data-testid="branding-preview">
              <h2 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>
                How it looks
              </h2>
              <p
                style={{
                  color: 'var(--text-muted)',
                  fontSize: '0.85rem',
                  margin: '0.35rem 0 1rem',
                }}
              >
                The real colours your bidders will see, worked out the same way your site and app
                work them out.
              </p>

              <div
                style={{
                  borderRadius: 'var(--r-tile, 0.375rem)',
                  overflow: 'hidden',
                  border: '1px solid var(--admin-hairline, var(--border))',
                }}
              >
                {/* The brand band, with the logo on it exactly as the app draws it. */}
                <div
                  data-testid="preview-band"
                  style={{
                    background: preview.primary,
                    padding: '0.9rem 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '4rem',
                  }}
                >
                  {form.logoUrl !== '' ? (
                    <img
                      src={form.logoUrl}
                      alt=""
                      style={{ height: '2.5rem', width: 'auto', maxWidth: '12.5rem' }}
                    />
                  ) : (
                    <span style={{ color: preview.primaryFg, fontWeight: 600 }}>
                      {tenants.find((t) => t.id === selectedId)?.name ?? 'Your house'}
                    </span>
                  )}
                </div>

                {/* The dark panel, and the muted step on it. */}
                <div style={{ background: preview.deep, padding: '0.9rem 1rem' }}>
                  <div style={{ color: preview.deepFg, fontWeight: 600, fontSize: '0.9rem' }}>
                    Spring Tool Sale
                  </div>
                  <div
                    data-testid="preview-deep-muted"
                    style={{ color: preview.deepMuted, fontSize: '0.8rem', marginTop: '0.15rem' }}
                  >
                    128 lots · closes Sunday
                  </div>
                </div>

                {/* The CTA, derived from the accent by the shared function. */}
                <div
                  style={{
                    background: 'var(--surface)',
                    padding: '0.9rem 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                  }}
                >
                  <span
                    data-testid="preview-cta"
                    style={{
                      background: preview.ctaBg,
                      color: preview.ctaFg,
                      padding: '0.45rem 0.9rem',
                      borderRadius: 'var(--r-tile, 0.375rem)',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                    }}
                  >
                    Place bid
                  </span>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                    {preview.deepIsStored
                      ? 'Dark panels use your own dark colour.'
                      : 'Dark panels are darkened from your main colour.'}
                  </span>
                </div>
              </div>
            </section>
          ) : null}

          {/* ─── Look and feel ──────────────────────────────────────────── */}
          <section style={CARD}>
            <h2 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>
              Look and feel
            </h2>
            <p
              style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0.35rem 0 1rem' }}
            >
              Changes the type and the shape of your pages. It never changes your colours, and never
              moves anything — so whichever you pick, every page still works.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {BIDDER_PRESETS.map((p) => (
                <label
                  key={p}
                  style={{
                    display: 'flex',
                    gap: '0.75rem',
                    alignItems: 'flex-start',
                    padding: '0.85rem',
                    border: `1px solid ${
                      form.preset === p
                        ? 'var(--brand-accent)'
                        : 'var(--admin-hairline, var(--border))'
                    }`,
                    borderRadius: 'var(--r-tile, 0.375rem)',
                    cursor: 'pointer',
                    background: form.preset === p ? 'var(--surface-muted)' : 'transparent',
                  }}
                >
                  <input
                    type="radio"
                    name="bidder-preset"
                    checked={form.preset === p}
                    onChange={() => {
                      set('preset', p);
                    }}
                    style={{ marginTop: '0.2rem' }}
                  />
                  <span>
                    <b style={{ color: 'var(--text)', fontSize: '0.92rem' }}>
                      {BIDDER_PRESET_LABELS[p]}
                      {p === 'modern' ? ' (default)' : ''}
                    </b>
                    <span
                      style={{
                        display: 'block',
                        color: 'var(--text-muted)',
                        fontSize: '0.85rem',
                        marginTop: '0.15rem',
                      }}
                    >
                      {BIDDER_PRESET_BLURBS[p]}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </section>

          {error !== null ? (
            <p
              role="alert"
              style={{
                color: 'var(--text-danger, #991b1b)',
                fontSize: '0.85rem',
                marginTop: '1rem',
              }}
            >
              {error}
            </p>
          ) : null}

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
            <Button
              onClick={() => {
                void save();
              }}
              disabled={blocked}
            >
              {saving ? 'Saving…' : uploading ? 'Uploading…' : 'Save'}
            </Button>
            {dirty ? (
              <Button
                variant="outline"
                onClick={() => {
                  setForm(saved);
                }}
                disabled={saving}
              >
                Cancel
              </Button>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * One colour: a swatch, a hex box, and — where the colour is optional — a way
 * back to the default.
 *
 * The swatch and the text box are two views of one value rather than two values.
 * `<input type="color">` can only hold `#rrggbb`, so a three-digit shorthand the
 * owner types is EXPANDED for the swatch while their own text stays as typed;
 * feeding the raw value straight in would show black beside a perfectly good
 * `#abc`.
 */
function ColourField({
  label,
  hint,
  value,
  onChange,
  error,
  disabled,
  clearable = false,
  testId,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
  /* `| undefined` explicitly: this repo runs `exactOptionalPropertyTypes`, so an
     optional prop and a prop that may BE undefined are different types, and the
     errors map hands us the second. */
  error?: string | undefined;
  disabled: boolean;
  clearable?: boolean;
  testId: string;
}) {
  const swatch = normaliseHex(value) ?? '#ffffff';
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label
        style={{
          display: 'block',
          fontSize: '0.8rem',
          fontWeight: 600,
          color: 'var(--text)',
          marginBottom: '0.15rem',
        }}
      >
        {label}
      </label>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', margin: '0 0 0.4rem' }}>{hint}</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <input
          type="color"
          aria-label={`${label} swatch`}
          value={swatch}
          disabled={disabled}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          style={{
            width: '2.25rem',
            height: '2.25rem',
            padding: 0,
            border: '1px solid var(--admin-hairline, var(--border))',
            borderRadius: '0.375rem',
            background: 'none',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        />
        <input
          type="text"
          inputMode="text"
          spellCheck={false}
          aria-label={label}
          data-testid={testId}
          value={value}
          disabled={disabled}
          placeholder="#556677"
          onChange={(e) => {
            onChange(e.target.value);
          }}
          style={{
            flex: '0 0 9rem',
            padding: '0.45rem 0.6rem',
            border: `1px solid ${error ? 'var(--text-danger, #991b1b)' : 'var(--admin-hairline, var(--border))'}`,
            borderRadius: '0.375rem',
            background: 'var(--surface)',
            color: 'var(--text)',
            fontSize: '0.85rem',
            fontFamily: 'var(--font-mono, ui-monospace, monospace)',
          }}
        />
        {clearable && value.trim() !== '' ? (
          <button
            type="button"
            onClick={() => {
              onChange('');
            }}
            disabled={disabled}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--text-muted)',
              fontSize: '0.8rem',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
        ) : null}
      </div>
      {error !== undefined ? (
        <p
          role="alert"
          style={{ color: 'var(--text-danger, #991b1b)', fontSize: '0.78rem', marginTop: '0.3rem' }}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}
